import type { WorkerOp, WorkerReply } from "./routingWorkerProtocol";

export interface WorkerLike {
  postMessage(message: unknown): void;
  terminate(): void;
  onmessage: ((e: { data: unknown }) => void) | null;
  onerror: ((e: unknown) => void) | null;
}

// Воркер умер/не запустился: вызывающий переходит на встроенный движок насовсем.
export class WorkerFailure extends Error {}

// Запросы к воркеру по id. Ошибка воркера (onerror) отклоняет все ожидающие
// вызовы, глушит воркер, и дальнейшие call() падают сразу - см. фасад
// composables/useClientRouting.ts. Ответ ok:false отклоняет только свой вызов.
export class RoutingWorkerClient {
  private worker: WorkerLike | null;
  private seq = 0;
  private pending = new Map<number, { resolve: (v: unknown) => void; reject: (e: Error) => void }>();
  private broken = false;

  constructor(factory: () => WorkerLike, apiBase: string) {
    const w = factory();
    this.worker = w;
    w.onmessage = (e) => this.onReply(e.data as WorkerReply);
    w.onerror = (e) => this.fail(new WorkerFailure(`routing worker error: ${(e as { message?: string })?.message ?? "unknown"}`));
    this.call("init", { apiBase }).catch(() => {});
  }

  call<T = unknown>(op: WorkerOp, payload: Record<string, unknown> = {}): Promise<T> {
    if (this.broken || !this.worker) return Promise.reject(new WorkerFailure("routing worker is not available"));
    const id = ++this.seq;
    return new Promise<T>((resolve, reject) => {
      this.pending.set(id, { resolve: resolve as (v: unknown) => void, reject });
      this.worker!.postMessage({ id, op, ...payload });
    });
  }

  private onReply(reply: WorkerReply) {
    const p = this.pending.get(reply.id);
    if (!p) return;
    this.pending.delete(reply.id);
    if (reply.ok) p.resolve(reply.result);
    else p.reject(new Error(reply.error));
  }

  private fail(err: Error) {
    if (this.broken) return;
    this.broken = true;
    for (const p of this.pending.values()) p.reject(err);
    this.pending.clear();
    this.worker?.terminate();
  }
}
